import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { useSelector } from "react-redux";
import { SOCKET_URL } from "../utils/constants";

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [userdata, setUserdata] = useState({});
  const [userId, setUserId] = useState("");

  const { token, user } = useSelector((state) => state.auth);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const socketRef = useRef(null);
  const maxReconnectAttempts = Infinity; 
  const baseReconnectDelay = 1000; 

  // Safe user data processing
  useEffect(() => {
    if (!user) return;

    if (typeof user === "string") {
      try {
        const parsedUser = JSON.parse(user);
        if (parsedUser && typeof parsedUser === "object") {
          setUserdata(parsedUser);
        } else {
          console.warn("Parsed user is not an object");
        }
      } catch (error) {
        console.error("Failed to parse user JSON:", error);
      }
    } else if (typeof user === "object" && user !== null) {
      setUserdata(user);
    } else {
      console.warn("Invalid user format:", user);
    }
  }, [user]);

  useEffect(() => {
    if (userdata) {
      setUserId(userdata.user ? userdata.user._id : userdata._id);
    }
  }, [userdata]);

  // Cleanup function (stable - uses refs only)
  const cleanup = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Calculate exponential backoff delay (stable - pure function)
  const getReconnectDelay = useCallback((attempt) => {
    return Math.min(baseReconnectDelay * Math.pow(2, attempt), 30000); // Max 30 seconds
  }, []);

  // Initialize or reconnect socket
  const initializeSocket = useCallback((tokenValue, userIdValue) => {
    // Cleanup existing socket
    const currentSocket = socketRef.current;
    if (currentSocket) {
      cleanup();
      currentSocket.removeAllListeners();
      currentSocket.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    }

    if (!tokenValue || !userIdValue) {
      return;
    }

    // Get token from nested structure if needed
    const actualToken = tokenValue?.token?.token || tokenValue?.token || tokenValue;

    if (!actualToken) {
      console.warn("No token available for socket connection");
      return;
    }

    console.log("🔌 Initializing socket connection...");

    const newSocket = io(SOCKET_URL, {
      query: { token: actualToken },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: baseReconnectDelay,
      reconnectionDelayMax: 30000,
      timeout: 20000,
      transports: ["websocket", "polling"],
    });
    console.log(newSocket);

    // Connection successful
    newSocket.on("connect", () => {
      console.log("✅ Socket connected:", newSocket.id);
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;
      sessionStorage.setItem("socketId", newSocket.id);

      // Emit reconnection event
      if (userIdValue) {
        newSocket.emit("tryingToReconnect", userIdValue);
      }

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      pingIntervalRef.current = setInterval(() => {
        if (newSocket.connected) {
          newSocket.emit("ping");
        }
      }, 10000);
    });

    newSocket.on("connect_error", (error) => {
      console.error("❌ Socket connection error:", error.message);
      setIsConnected(false);
    });

    newSocket.on("disconnect", (reason) => {
      console.warn("⚠️ Socket disconnected:", reason);
      setIsConnected(false);
      cleanup();

      if (reason === "io server disconnect" || reason === "transport close") {
        reconnectAttemptsRef.current++;
        const delay = getReconnectDelay(reconnectAttemptsRef.current);
        console.log(`🔄 Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current})...`);

        reconnectTimeoutRef.current = setTimeout(() => {
          if (!newSocket.connected && tokenValue && userIdValue) {
            console.log("🔄 Manual reconnection attempt...");
            newSocket.connect();
          }
        }, delay);
      }
    });

    // Reconnection attempt
    newSocket.on("reconnect_attempt", (attemptNumber) => {
      console.log(`🔄 Reconnection attempt ${attemptNumber}...`);
      reconnectAttemptsRef.current = attemptNumber;
    });

    // Reconnection successful
    newSocket.on("reconnect", (attemptNumber) => {
      console.log(`✅ Reconnected after ${attemptNumber} attempts`);
      reconnectAttemptsRef.current = 0;
      setIsConnected(true);
    });

    // Reconnection failed
    newSocket.on("reconnect_failed", () => {
      console.error("❌ Reconnection failed");
      setIsConnected(false);
    });

    // Reconnection error
    newSocket.on("reconnect_error", (error) => {
      console.error("❌ Reconnection error:", error.message);
    });

    socketRef.current = newSocket;
    setSocket(newSocket);
  }, [cleanup, getReconnectDelay]);

  useEffect(() => {
    if (!token || !userId) {
      const currentSocket = socketRef.current;
      if (currentSocket) {
        cleanup();
        currentSocket.removeAllListeners();
        currentSocket.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    initializeSocket(token, userId);

    return () => {
      cleanup();
      const currentSocket = socketRef.current;
      if (currentSocket) {
        currentSocket.removeAllListeners();
        currentSocket.disconnect();
        socketRef.current = null;
      }
    };
  }, [token, userId, initializeSocket]);

  const socketOn = useCallback((event, callback) => {
    if (!socket) {
      console.warn(`⚠️ Cannot listen to "${event}" - socket not connected`);
      return () => { }; 
    }

    socket.on(event, callback);

    // Return cleanup function
    return () => {
      socket.off(event, callback);
    };
  }, [socket]);

  const socketEmit = useCallback((event, ...args) => {
    if (!socket || !isConnected) {
      console.warn(`⚠️ Cannot emit "${event}" - socket not connected`);
      return false;
    }

    socket.emit(event, ...args);
    return true;
  }, [socket, isConnected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      const currentSocket = socketRef.current;
      if (currentSocket) {
        currentSocket.removeAllListeners();
        currentSocket.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        socketOn,
        socketEmit,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);